// Context for managing dashboard display mode (standard vs all-in-one)

import React, { createContext, useContext, useState, useEffect } from 'react';

interface DashboardModeContextType {
  isAllInOneMode: boolean;
  toggleAllInOneMode: () => void;
}

const DashboardModeContext = createContext<DashboardModeContextType | undefined>(undefined);

export const useDashboardMode = () => {
  const context = useContext(DashboardModeContext);
  if (context === undefined) {
    throw new Error('useDashboardMode must be used within a DashboardModeProvider');
  }
  return context;
};

interface DashboardModeProviderProps {
  children: React.ReactNode;
}

export const DashboardModeProvider: React.FC<DashboardModeProviderProps> = ({ children }) => {
  const [isAllInOneMode, setIsAllInOneMode] = useState(() => {
    // Load preference from localStorage
    const saved = localStorage.getItem('dashboardAllInOneMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Save preference to localStorage when changed
  useEffect(() => {
    localStorage.setItem('dashboardAllInOneMode', JSON.stringify(isAllInOneMode));
  }, [isAllInOneMode]);

  const toggleAllInOneMode = () => {
    setIsAllInOneMode(!isAllInOneMode);
  };

  return (
    <DashboardModeContext.Provider value={{ isAllInOneMode, toggleAllInOneMode }}>
      {children}
    </DashboardModeContext.Provider>
  );
};
